import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Badge,
  type BadgeTone,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Textarea,
} from "@jotty/ui";
import {
  ApiError,
  meetings,
  type DeliveryReceipt,
  type MeetingDetail,
  type MeetingStatus,
} from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";

export const Route = createFileRoute("/_authed/meetings/$id")({
  component: MeetingDetailPage,
});

const statusTone: Record<MeetingStatus, BadgeTone> = {
  scheduled: "neutral",
  in_progress: "warning",
  completed: "success",
  failed: "danger",
  archived: "neutral",
  no_transcript: "neutral",
  too_short: "neutral",
  summary_failed: "neutral",
  summarized: "success",
  recording: "warning",
};

function detectPlatformFromURL(url: string): string {
  if (url.includes("zoom.us")) return "Zoom";
  if (url.includes("meet.google.com")) return "Google Meet";
  if (url.includes("teams.microsoft.com")) return "Teams";
  return "Meeting";
}

function formatDuration(start: string, end: string): string {
  const mins = Math.round(
    (new Date(end).getTime() - new Date(start).getTime()) / 60000,
  );
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// ---------------------------------------------------------------------------
// Types for edit state
// ---------------------------------------------------------------------------

interface ActionItemEdit {
  owner: string;
  task: string;
  due_date: string;
}

interface TopicEdit {
  title: string;
  duration_minutes: number;
  notes: string;
}

const CHANNEL_LABELS: Record<string, string> = {
  email: "Email",
  slack: "Slack",
  google_docs: "Docs",
};

// Collapse a receipt history (newest-first) to the latest entry per channel.
function latestPerChannel(recs: DeliveryReceipt[] | undefined): DeliveryReceipt[] {
  if (!recs?.length) return [];
  const seen = new Map<string, DeliveryReceipt>();
  for (const r of recs) {
    if (!seen.has(r.channel)) seen.set(r.channel, r);
  }
  return [...seen.values()];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function MeetingDetailPage() {
  const { id } = Route.useParams();
  const tenantsList = useAuthStore((s) => s.tenants);
  const activeTenant = tenantsList[0];

  const [meeting, setMeeting] = useState<MeetingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [receipts, setReceipts] = useState<DeliveryReceipt[] | null>(null);
  const [delivering, setDelivering] = useState(false);
  const [retryingChannel, setRetryingChannel] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);

  // Edit state — mirrors summary content
  const [editHeadline, setEditHeadline] = useState("");
  const [editDecisions, setEditDecisions] = useState<string[]>([]);
  const [editActionItems, setEditActionItems] = useState<ActionItemEdit[]>([]);
  const [editTopics, setEditTopics] = useState<TopicEdit[]>([]);
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeTenant) return;
    let cancelled = false;
    (async () => {
      try {
        const m = await meetings.get(activeTenant.id, id);
        if (!cancelled) setMeeting(m);
      } catch (err) {
        if (!cancelled)
          setError(
            err instanceof ApiError ? err.message : "Could not load meeting",
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTenant?.id, id]);

  const refresh = async () => {
    if (!activeTenant) return;
    try {
      const m = await meetings.get(activeTenant.id, id);
      setMeeting(m);
    } catch {
      setError("Could not refresh meeting. Please retry.");
    }
  };

  const enterEditMode = () => {
    if (!meeting?.summary) return;
    const c = meeting.summary.content;
    const m = meeting;
    setEditHeadline(c.headline ?? "");
    setEditDecisions(c.decisions ?? []);
    setEditActionItems(
      (c.action_items ?? []).map((ai) => ({
        owner: ai.owner ?? "",
        task: ai.task ?? "",
        due_date: ai.due_date ?? "",
      })),
    );
    setEditTopics(
      (m.summary?.content?.topic_breakdown ?? []).map((t) => ({
        title: t.title ?? "",
        duration_minutes: t.duration_minutes ?? 0,
        notes: t.notes ?? "",
      })),
    );
    setEditNotes(c.notes ?? "");
    setSaveError(null);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!activeTenant) return;
    setSaving(true);
    setSaveError(null);
    try {
      await meetings.putSummary(activeTenant.id, id, {
        content: {
          headline: editHeadline,
          decisions: editDecisions.filter(Boolean),
          action_items: editActionItems
            .filter((ai) => ai.task.trim())
            .map((ai) => ({
              owner: ai.owner,
              task: ai.task,
              due_date: ai.due_date || undefined,
            })),
          notes: editNotes || undefined,
          topic_breakdown: editTopics
            .filter((t) => t.title.trim())
            .map((t) => ({
              title: t.title,
              duration_minutes: t.duration_minutes,
              notes: t.notes || undefined,
            })),
        },
      });
      setEditMode(false);
      void refresh();
    } catch (err) {
      setSaveError(
        err instanceof ApiError ? err.message : "Could not save summary",
      );
    } finally {
      setSaving(false);
    }
  };

  const [regenerating, setRegenerating] = useState(false);
  const [regenNote, setRegenNote] = useState<string | null>(null);

  const handleRegenerate = async () => {
    if (!activeTenant) return;
    setRegenerating(true);
    setRegenNote(null);
    try {
      await meetings.regenerateSummary(activeTenant.id, id);
      setRegenNote(
        "Summary queued — refresh in a minute to see the new version.",
      );
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not queue regeneration",
      );
    } finally {
      setRegenerating(false);
    }
  };

  const handleReDeliver = async () => {
    if (!activeTenant) return;
    setDelivering(true);
    setReceipts(null);
    try {
      const r = await meetings.deliver(activeTenant.id, id, [
        "email",
        "slack",
        "google_docs",
      ]);
      setReceipts(r.receipts);
      void refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Delivery failed");
    } finally {
      setDelivering(false);
    }
  };

  const handleRetryChannel = async (channel: DeliveryReceipt["channel"]) => {
    if (!activeTenant) return;
    setRetryingChannel(channel);
    try {
      const r = await meetings.deliver(activeTenant.id, id, { channel });
      const updated = r.receipts[0];
      if (updated) {
        setReceipts((prev) => {
          const base = prev ?? latestPerChannel(meeting?.deliveries);
          const next = base.filter((x) => x.channel !== channel);
          next.push(updated);
          return next;
        });
      }
      void refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Delivery failed");
    } finally {
      setRetryingChannel(null);
    }
  };

  if (!activeTenant) return null;
  if (error && !meeting) return <Banner tone="danger">{error}</Banner>;
  if (!meeting)
    return (
      <p className="text-sm text-[color:var(--color-text-muted)]">Loading…</p>
    );

  const meetingAny = meeting;

  return (
    <div className="space-y-4">
      {/* Back */}
      <Link
        to="/meetings"
        className="text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
      >
        ← Meetings
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--color-text)]">
            {meeting.title}
          </h1>
          {/* Status row */}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[color:var(--color-text-muted)]">
            <Badge tone={statusTone[meeting.status]}>
              {meeting.status.replace(/_/g, " ")}
            </Badge>
            {meetingAny.meeting_url ? (
              <Badge tone="neutral">
                {detectPlatformFromURL(meetingAny.meeting_url)}
              </Badge>
            ) : null}
            <Badge tone="primary">
              {meeting.language === "id"
                ? "🇮🇩"
                : meeting.language === "en"
                  ? "🇬🇧"
                  : "🌐"}{" "}
              {meeting.language}
            </Badge>
            {meeting.summary?.transcript_trimmed ? (
              <Badge tone="neutral">
                long meeting · summarized in 2 passes
              </Badge>
            ) : null}
            <span>· {meeting.attendees.length} attendees</span>
            {meeting.started_at && meetingAny.ended_at ? (
              <span>
                · {formatDuration(meeting.started_at, meetingAny.ended_at)}
              </span>
            ) : null}
            {meeting.started_at ? (
              <span>· {new Date(meeting.started_at).toLocaleString()}</span>
            ) : (
              <span>
                · created {new Date(meeting.created_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        {/* Header actions */}
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void handleRegenerate()}
            disabled={regenerating || !!meeting.desktop_notes}
          >
            {regenerating ? "Queuing…" : "Regenerate"}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void handleReDeliver()}
            disabled={delivering || !meeting.summary}
          >
            {delivering ? "Delivering…" : "Re-deliver"}
          </Button>
          {editMode ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={cancelEdit}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={enterEditMode}
              disabled={!meeting.summary}
            >
              Edit
            </Button>
          )}
          {/* Overflow menu stub */}
          <button className="rounded-lg border border-[color:var(--color-border)] px-2 py-1.5 text-xs hover:bg-[color:var(--color-bg)]">
            ···
          </button>
        </div>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}
      {saveError ? <Banner tone="danger">{saveError}</Banner> : null}
      {regenNote ? <Banner tone="info">{regenNote}</Banner> : null}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
        {/* Main column */}
        <div className="space-y-4">
          {meeting.desktop_notes ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Your notes</CardTitle>
                </CardHeader>
                <CardBody>
                  <p className="whitespace-pre-wrap">
                    {meeting.desktop_notes.raw_notes || "No typed notes yet."}
                  </p>
                </CardBody>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Enhanced notes</CardTitle>
                </CardHeader>
                <CardBody>
                  <p className="whitespace-pre-wrap">
                    {meeting.desktop_notes.enhanced_notes ||
                      "Open Jotty desktop to enhance your notes after capture."}
                  </p>
                  <p className="mt-4 text-sm text-muted">
                    Capture: {meeting.desktop_notes.capture_phase}. Synced note
                    version {meeting.desktop_notes.version}.
                  </p>
                </CardBody>
              </Card>
            </>
          ) : meeting.summary ? (
            <>
              {/* TL;DR / Headline */}
              <Card>
                <CardHeader>
                  <CardTitle>TL;DR</CardTitle>
                </CardHeader>
                <CardBody>
                  {editMode ? (
                    <Input
                      value={editHeadline}
                      onChange={(e) => setEditHeadline(e.target.value)}
                      placeholder="One-sentence summary…"
                    />
                  ) : (
                    <p className="text-base text-[color:var(--color-text)]">
                      {meeting.summary.content.headline}
                    </p>
                  )}
                </CardBody>
              </Card>

              {/* Key decisions */}
              <Card>
                <CardHeader>
                  <CardTitle>Key decisions</CardTitle>
                  {editMode ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setEditDecisions((d) => [...d, ""])}
                    >
                      + Add
                    </Button>
                  ) : null}
                </CardHeader>
                <CardBody>
                  {editMode ? (
                    <ul className="space-y-2">
                      {editDecisions.map((d, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <div className="flex flex-col gap-0.5">
                            <button
                              onClick={() => {
                                if (i === 0) return;
                                const next = [...editDecisions];
                                [next[i - 1], next[i]] = [
                                  next[i]!,
                                  next[i - 1]!,
                                ];
                                setEditDecisions(next);
                              }}
                              className="text-[10px] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)] disabled:opacity-30"
                              disabled={i === 0}
                            >
                              ▲
                            </button>
                            <button
                              onClick={() => {
                                if (i === editDecisions.length - 1) return;
                                const next = [...editDecisions];
                                [next[i], next[i + 1]] = [
                                  next[i + 1]!,
                                  next[i]!,
                                ];
                                setEditDecisions(next);
                              }}
                              className="text-[10px] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)] disabled:opacity-30"
                              disabled={i === editDecisions.length - 1}
                            >
                              ▼
                            </button>
                          </div>
                          <Input
                            value={d}
                            onChange={(e) => {
                              const next = [...editDecisions];
                              next[i] = e.target.value;
                              setEditDecisions(next);
                            }}
                            placeholder={`Decision ${i + 1}`}
                          />
                          <button
                            onClick={() =>
                              setEditDecisions((prev) =>
                                prev.filter((_, j) => j !== i),
                              )
                            }
                            className="text-xs text-[color:var(--color-danger)]"
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                      {editDecisions.length === 0 ? (
                        <p className="text-xs text-[color:var(--color-text-muted)]">
                          No decisions yet.
                        </p>
                      ) : null}
                    </ul>
                  ) : meeting.summary.content.decisions &&
                    meeting.summary.content.decisions.length > 0 ? (
                    <ul className="space-y-2">
                      {meeting.summary.content.decisions.map((d, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm text-[color:var(--color-text)]"
                        >
                          <span className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full bg-[color:var(--color-primary)]/10 text-center text-[10px] font-bold text-[color:var(--color-primary)]">
                            {i + 1}
                          </span>
                          {d}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-[color:var(--color-text-muted)]">
                      No decisions recorded.
                    </p>
                  )}
                </CardBody>
              </Card>

              {/* Action items */}
              <Card>
                <CardHeader>
                  <CardTitle>Action items</CardTitle>
                  {editMode ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        setEditActionItems((prev) => [
                          ...prev,
                          { owner: "", task: "", due_date: "" },
                        ])
                      }
                    >
                      + Add
                    </Button>
                  ) : null}
                </CardHeader>
                <CardBody>
                  {editMode ? (
                    <ul className="space-y-3">
                      {editActionItems.map((ai, i) => (
                        <li
                          key={i}
                          className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-end"
                        >
                          <Field label={i === 0 ? "Task" : undefined}>
                            <Input
                              value={ai.task}
                              onChange={(e) => {
                                const next = [...editActionItems];
                                next[i] = {
                                  owner: next[i]!.owner,
                                  task: e.target.value,
                                  due_date: next[i]!.due_date,
                                };
                                setEditActionItems(next);
                              }}
                              placeholder="Task description"
                            />
                          </Field>
                          <Field label={i === 0 ? "Owner" : undefined}>
                            <Input
                              value={ai.owner}
                              onChange={(e) => {
                                const next = [...editActionItems];
                                next[i] = {
                                  owner: e.target.value,
                                  task: next[i]!.task,
                                  due_date: next[i]!.due_date,
                                };
                                setEditActionItems(next);
                              }}
                              placeholder="Owner"
                            />
                          </Field>
                          <Field label={i === 0 ? "Due date" : undefined}>
                            <Input
                              value={ai.due_date}
                              onChange={(e) => {
                                const next = [...editActionItems];
                                next[i] = {
                                  owner: next[i]!.owner,
                                  task: next[i]!.task,
                                  due_date: e.target.value,
                                };
                                setEditActionItems(next);
                              }}
                              placeholder="e.g. 2026-09-01"
                            />
                          </Field>
                          <button
                            onClick={() =>
                              setEditActionItems((prev) =>
                                prev.filter((_, j) => j !== i),
                              )
                            }
                            className="mb-0.5 text-xs text-[color:var(--color-danger)]"
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                      {editActionItems.length === 0 ? (
                        <p className="text-xs text-[color:var(--color-text-muted)]">
                          No action items yet.
                        </p>
                      ) : null}
                    </ul>
                  ) : meeting.summary.content.action_items &&
                    meeting.summary.content.action_items.length > 0 ? (
                    <ul className="space-y-2">
                      {meeting.summary.content.action_items.map((ai, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 accent-[color:var(--color-primary)]"
                            readOnly
                          />
                          <div>
                            <span className="text-[color:var(--color-text)]">
                              {ai.task}
                            </span>
                            <div className="mt-1 flex gap-2">
                              <span className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[10px] text-[color:var(--color-text-muted)]">
                                {ai.owner}
                              </span>
                              {ai.due_date ? (
                                <span className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[10px] text-[color:var(--color-text-muted)]">
                                  {ai.due_date}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-[color:var(--color-text-muted)]">
                      No action items recorded.
                    </p>
                  )}
                </CardBody>
              </Card>

              {/* Topic breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Topic breakdown</CardTitle>
                  {editMode ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        setEditTopics((prev) => [
                          ...prev,
                          { title: "", duration_minutes: 0, notes: "" },
                        ])
                      }
                    >
                      + Add
                    </Button>
                  ) : null}
                </CardHeader>
                <CardBody>
                  {editMode ? (
                    <ul className="space-y-3">
                      {editTopics.map((t, i) => (
                        <li key={i} className="flex items-end gap-2">
                          <Field
                            label={i === 0 ? "Topic" : undefined}
                            className="flex-1"
                          >
                            <Input
                              value={t.title}
                              onChange={(e) => {
                                const next = [...editTopics];
                                next[i] = {
                                  title: e.target.value,
                                  duration_minutes: next[i]!.duration_minutes,
                                  notes: next[i]!.notes,
                                };
                                setEditTopics(next);
                              }}
                              placeholder="Topic title"
                            />
                          </Field>
                          <Field label={i === 0 ? "Duration (min)" : undefined}>
                            <Input
                              type="number"
                              value={t.duration_minutes}
                              onChange={(e) => {
                                const next = [...editTopics];
                                next[i] = {
                                  title: next[i]!.title,
                                  duration_minutes: Number(e.target.value),
                                  notes: next[i]!.notes,
                                };
                                setEditTopics(next);
                              }}
                              placeholder="0"
                            />
                          </Field>
                          <button
                            onClick={() =>
                              setEditTopics((prev) =>
                                prev.filter((_, j) => j !== i),
                              )
                            }
                            className="mb-0.5 text-xs text-[color:var(--color-danger)]"
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                      {editTopics.length === 0 ? (
                        <p className="text-xs text-[color:var(--color-text-muted)]">
                          No topics yet.
                        </p>
                      ) : null}
                    </ul>
                  ) : meetingAny.summary?.content?.topic_breakdown &&
                    meetingAny.summary.content.topic_breakdown.length > 0 ? (
                    <ul className="space-y-2">
                      {(
                        meetingAny.summary.content.topic_breakdown as Array<{
                          title: string;
                          duration_minutes: number;
                          notes?: string;
                        }>
                      ).map((t, i) => (
                        <li
                          key={i}
                          className="flex items-start justify-between gap-4 text-sm"
                        >
                          <div className="flex items-start gap-2">
                            <span className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full bg-[color:var(--color-primary)]/10 text-center text-[10px] font-bold text-[color:var(--color-primary)]">
                              {i + 1}
                            </span>
                            <div>
                              <span className="font-medium text-[color:var(--color-text)]">
                                {t.title}
                              </span>
                              {t.notes ? (
                                <p className="mt-0.5 text-xs text-[color:var(--color-text-muted)]">
                                  {t.notes}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <span className="flex-shrink-0 text-xs text-[color:var(--color-text-muted)]">
                            {t.duration_minutes}m
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-[color:var(--color-text-muted)]">
                      No topics recorded.
                    </p>
                  )}
                </CardBody>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader>
                  <CardTitle>Notes</CardTitle>
                </CardHeader>
                <CardBody>
                  {editMode ? (
                    <Textarea
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Free-form notes…"
                    />
                  ) : meeting.summary.content.notes ? (
                    <p className="whitespace-pre-wrap text-sm text-[color:var(--color-text-subtle)]">
                      {meeting.summary.content.notes}
                    </p>
                  ) : (
                    <p className="text-xs text-[color:var(--color-text-muted)]">
                      No notes.
                    </p>
                  )}
                </CardBody>
              </Card>

              {/* Transcript link (edit mode only — PRO-42) */}
              {editMode && meetingAny.meeting_url ? (
                <p className="text-xs text-[color:var(--color-text-muted)]">
                  Original meeting:{" "}
                  <a
                    href={meetingAny.meeting_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-[color:var(--color-text)]"
                  >
                    {meetingAny.meeting_url}
                  </a>
                </p>
              ) : null}
            </>
          ) : (
            <Card>
              <CardBody>
                <EmptyState
                  title="No summary yet"
                  description="Write or paste a structured summary to enable delivery."
                  action={<Button onClick={enterEditMode}>Add summary</Button>}
                />
              </CardBody>
            </Card>
          )}
        </div>

        {/* Right rail */}
        <div className="space-y-4">
          {/* Delivery panel */}
          <Card>
            <CardHeader>
              <CardTitle>Delivery</CardTitle>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void handleReDeliver()}
                disabled={delivering || !meeting.summary}
              >
                {delivering ? "…" : "Re-deliver all"}
              </Button>
            </CardHeader>
            <CardBody className="space-y-2">
              {(() => {
                const rows = receipts ?? latestPerChannel(meeting.deliveries);
                if (rows.length === 0) {
                  return (
                    <p className="text-xs text-[color:var(--color-text-muted)]">
                      {meeting.summary
                        ? "Click Re-deliver all to send."
                        : "Add a summary first."}
                    </p>
                  );
                }
                return rows.map((r) => (
                  <div
                    key={r.channel}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="text-xs text-[color:var(--color-text)]">
                      {CHANNEL_LABELS[r.channel] ?? r.channel}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Badge
                        tone={
                          r.status === "ok"
                            ? "success"
                            : r.status === "skipped"
                              ? "warning"
                              : "danger"
                        }
                        title={r.error_reason || undefined}
                      >
                        {r.status}
                      </Badge>
                      {r.status !== "ok" && (
                        <button
                          type="button"
                          onClick={() => void handleRetryChannel(r.channel)}
                          disabled={
                            delivering || retryingChannel === r.channel || !meeting.summary
                          }
                          className="text-xs text-[color:var(--color-text-muted)] underline underline-offset-2 disabled:opacity-50"
                        >
                          {retryingChannel === r.channel ? "…" : "Retry"}
                        </button>
                      )}
                    </div>
                  </div>
                ));
              })()}
            </CardBody>
          </Card>

          {/* Attendees */}
          <Card>
            <CardHeader>
              <CardTitle>Attendees</CardTitle>
            </CardHeader>
            <CardBody>
              {meeting.attendees.length === 0 ? (
                <p className="text-xs text-[color:var(--color-text-muted)]">
                  No attendees.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {meeting.attendees.map((a) => (
                    <div
                      key={a}
                      title={a}
                      className="grid h-7 w-7 place-items-center rounded-full bg-[color:var(--color-primary)]/10 text-[10px] font-bold text-[color:var(--color-primary)]"
                    >
                      {a[0]?.toUpperCase()}
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Recording */}
          <Card>
            <CardHeader>
              <CardTitle>Recording</CardTitle>
            </CardHeader>
            <CardBody>
              {/* TODO: recording playback + auto-delete date after PRO-8 + PRO-20 */}
              <p className="text-xs text-[color:var(--color-text-muted)]">
                Available after recording is processed.
              </p>
            </CardBody>
          </Card>

          {/* Meta */}
          {meeting.summary ? (
            <Card>
              <CardHeader>
                <CardTitle>Meta</CardTitle>
              </CardHeader>
              <CardBody className="space-y-1.5 text-xs text-[color:var(--color-text-muted)]">
                {meetingAny.summary?.model ? (
                  <div className="flex justify-between gap-2">
                    <span>Model</span>
                    <span className="font-medium text-[color:var(--color-text)]">
                      {meetingAny.summary.model}
                    </span>
                  </div>
                ) : null}
                {meetingAny.summary?.prompt_version ? (
                  <div className="flex justify-between gap-2">
                    <span>Prompt</span>
                    <span className="font-medium text-[color:var(--color-text)]">
                      {meetingAny.summary.prompt_version}
                    </span>
                  </div>
                ) : null}
                {meetingAny.summary?.cost_usd ? (
                  <div className="flex justify-between gap-2">
                    <span>Cost</span>
                    <span className="font-medium text-[color:var(--color-text)]">
                      ${Number(meetingAny.summary.cost_usd).toFixed(4)}
                    </span>
                  </div>
                ) : null}
                {meeting.summary?.prompt_tokens != null ? (
                  <div className="flex justify-between gap-2">
                    <span>Tokens</span>
                    <span className="font-medium text-[color:var(--color-text)]">
                      {meeting.summary.prompt_tokens} in /{" "}
                      {meeting.summary.completion_tokens ?? 0} out
                    </span>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
